import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import notify from '../../../utils/notify';
import ActionButton from '../../../Components/Common/ActionButton';
import { SkeletonCard } from '../../../Components/Common/Skeleton';
import GtcViewer from '../../../Components/Gtc/GtcViewer';
import gtcApi from '../../../pages/Master/data/gtcApi';

// Read-only GTC of a procurement (fe#123 §4.2-§4.5). With procurementMitraId
// it loads that bidder's snapshot (TE); otherwise the caller's own view:
// buyer clauses for Panitia, buyer clauses + own amendments for Mitra.
const ProcurementGtcTab = ({
  procurementId,
  procurementMitraId,
  showAmend = false,
  onLoaded,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!procurementId) return undefined;
    let cancelled = false;
    setData(null);
    setIsLoading(true);
    (procurementMitraId
      ? gtcApi.getProcurementMitraGtc(procurementId, procurementMitraId)
      : gtcApi.getProcurementGtc(procurementId)
    )
      .then(result => {
        if (cancelled) return;
        setData(result);
        onLoaded?.(result);
      })
      .catch(err => {
        if (cancelled) return;
        setData(null);
        notify.apiError(err, 'load the GTC');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // onLoaded is a notification only; refetch on ids alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procurementId, procurementMitraId]);

  if (isLoading && !data) return <SkeletonCard lines={4} className='border' />;

  if (showAmend && data?.source === 'none') {
    return (
      <div className='gtcd-empty-inline'>
        This bidder has not submitted an offer yet.
      </div>
    );
  }

  const gtcs = data?.gtcs || [];

  return (
    <div className='d-flex flex-column gap-3'>
      {showAmend && data?.source === 'live' && gtcs.length > 0 && (
        <div className='d-flex flex-wrap justify-content-end gap-2'>
          {gtcs.map(gtc => (
            <ActionButton
              key={gtc.id}
              variant='secondary'
              size='sm'
              icon='ri-edit-2-line'
              onClick={() => navigate(`/apps-gtc-amend/dtl/${gtc.id}`)}
            >
              Edit in Master Amend GTC
              {gtcs.length > 1 ? ` (${gtc.gtcNumber})` : ''}
            </ActionButton>
          ))}
        </div>
      )}
      <GtcViewer
        gtcs={gtcs}
        showAmend={showAmend}
        source={data?.source || undefined}
        snapshotDate={data?.snapshotDate || undefined}
        emptyText='No GTC attached to this procurement.'
      />
    </div>
  );
};

ProcurementGtcTab.propTypes = {
  procurementId: PropTypes.string,
  procurementMitraId: PropTypes.string,
  showAmend: PropTypes.bool,
  onLoaded: PropTypes.func,
};

export default ProcurementGtcTab;
